/**
 * Deckbuilding rules. BLUEPRINT section 10.
 *
 * validateDeck takes a flat list of card ids (the same shape createGame takes)
 * so the deck builder, the validator CLI and the engine all agree.
 */

import type { CardDefinition, Chain } from './cardTypes';
import { CHAINS } from './types';

export const DECK_SIZE = 40;
export const MAX_COPIES = 3;
export const WALLET_SUGGEST_MIN = 15;
export const WALLET_SUGGEST_MAX = 18;
export const WALLET_WARN_LOW = 14;
export const WALLET_WARN_HIGH = 20;
/** A chain needs this many Wallets before the builder stops warning. */
export const MIN_WALLETS_PER_NEEDED_CHAIN = 6;

export interface DeckValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
  /** Useful for the builder UI. */
  stats: {
    size: number;
    wallets: number;
    walletsByChain: Record<string, number>;
    factions: string[];
    byType: Record<string, number>;
  };
}

export interface CardLookup {
  getCard(id: string): CardDefinition | undefined;
}

export function validateDeck(cards: string[], cardDb: CardLookup): DeckValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  const counts = new Map<string, number>();
  for (const id of cards) counts.set(id, (counts.get(id) ?? 0) + 1);

  const walletsByChain: Record<string, number> = {};
  const byType: Record<string, number> = {};
  const factions = new Set<string>();
  const chainDemand: Record<string, number> = {};
  let wallets = 0;

  for (const [id, count] of counts) {
    const card = cardDb.getCard(id);
    if (!card) {
      errors.push(`Unknown card id: ${id}`);
      continue;
    }
    byType[card.type] = (byType[card.type] ?? 0) + count;
    if (card.faction !== 'neutral') factions.add(card.faction);

    const basicWallet = card.type === 'wallet' && card.wallet?.basic === true;
    if (card.legendary) {
      if (count > 1) errors.push(`${card.name} is Legendary, so only 1 copy is allowed (found ${count}).`);
    } else if (!basicWallet && count > MAX_COPIES) {
      errors.push(`${card.name} exceeds the ${MAX_COPIES} copy limit (found ${count}).`);
    }

    if (card.type === 'wallet') {
      wallets += count;
      const chain = card.wallet?.chain ?? 'any';
      walletsByChain[chain] = (walletsByChain[chain] ?? 0) + count;
    }
    for (const c of CHAINS) {
      const need = card.cost[c] ?? 0;
      if (need > 0) chainDemand[c] = (chainDemand[c] ?? 0) + need * count;
    }
  }

  if (cards.length !== DECK_SIZE) {
    errors.push(`A deck must be exactly ${DECK_SIZE} cards (found ${cards.length}).`);
  }

  if (wallets < WALLET_WARN_LOW) {
    warnings.push(`Only ${wallets} Wallets. ${WALLET_SUGGEST_MIN} to ${WALLET_SUGGEST_MAX} is suggested.`);
  } else if (wallets > WALLET_WARN_HIGH) {
    warnings.push(`${wallets} Wallets is a lot. ${WALLET_SUGGEST_MIN} to ${WALLET_SUGGEST_MAX} is suggested.`);
  } else if (wallets < WALLET_SUGGEST_MIN || wallets > WALLET_SUGGEST_MAX) {
    warnings.push(`${wallets} Wallets is outside the suggested ${WALLET_SUGGEST_MIN} to ${WALLET_SUGGEST_MAX}.`);
  }

  if (factions.size >= 3) {
    for (const chain of Object.keys(chainDemand) as Chain[]) {
      const have = (walletsByChain[chain] ?? 0) + (walletsByChain.any ?? 0);
      if (have < MIN_WALLETS_PER_NEEDED_CHAIN) {
        warnings.push(
          `A ${factions.size} faction deck needs ${chain} Gas but has only ${have} Wallets that make it.`,
        );
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    stats: {
      size: cards.length,
      wallets,
      walletsByChain,
      factions: [...factions].sort(),
      byType,
    },
  };
}

/** Expand a { id, count } deck definition into the flat list createGame wants. */
export function expandDeck(entries: { id: string; count: number }[]): string[] {
  const out: string[] = [];
  for (const e of entries) {
    for (let i = 0; i < e.count; i++) out.push(e.id);
  }
  return out;
}
